import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Gavel, Clock, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Pro-side Negotiable Bids Inbox.
 * Shows every open-for-bids booking in the pro's category & city and lets
 * them submit a bid (or counter their existing bid).
 */
export default function NegotiableBidsInbox({ provider }) {
    const navigate = useNavigate();
    const [openJobs, setOpenJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeBids, setActiveBids] = useState({}); // booking_id → my bid
    const [bidModalJob, setBidModalJob] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try {
            // /providers/me/available-jobs returns BOTH pending and open_for_bids jobs.
            const { data } = await api.get("/providers/me/available-jobs");
            const open = (data || []).filter((j) => j.is_negotiable && j.status === "open_for_bids");
            setOpenJobs(open);
            // For each open booking, fetch the pro's existing bid (if any)
            const pairs = await Promise.all(
                open.map((j) =>
                    api
                        .get(`/bookings/${j.id}/bids`)
                        .then((r) => [j.id, (r.data || []).find((b) => b.provider_user_id === provider.user_id) || null])
                        .catch(() => [j.id, null])
                )
            );
            setActiveBids(Object.fromEntries(pairs));
        } catch (e) {
            // Treat 404 / no permission gracefully — likely no open jobs.
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (provider?.user_id) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider?.user_id]);

    if (loading) {
        return (
            <div className="bg-card border border-white/10 p-7 rounded-sm" data-testid="bids-inbox-loading">
                <div className="overline mb-3">Negotiable Jobs</div>
                <div className="text-sm text-muted-foreground">Loading…</div>
            </div>
        );
    }

    if (openJobs.length === 0) return null; // hide entirely when nothing to bid on

    return (
        <div
            className="bg-card border border-primary/30 p-7 lg:p-9 rounded-sm relative overflow-hidden"
            data-testid="bids-inbox"
        >
            <div className="absolute -top-24 -right-20 w-64 h-64 rounded-full bg-primary/8 blur-3xl pointer-events-none" />
            <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                    <Gavel size={14} className="gold-text" strokeWidth={1.5} />
                    <div className="overline">Negotiable Jobs · {openJobs.length} open</div>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl mb-6">
                    Bid on <span className="italic gold-grad">Royal trades.</span>
                </h3>
                <ul className="space-y-3">
                    {openJobs.map((j) => {
                        const myBid = activeBids[j.id];
                        return (
                            <li
                                key={j.id}
                                className="flex items-center gap-4 p-4 bg-background/60 border border-white/10 rounded-sm hover:border-primary/40 transition-colors"
                                data-testid={`bid-job-${j.id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium text-foreground truncate">
                                            {j.service_name}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-[0.2em] gold-text">
                                            ${j.total_amount?.toLocaleString() || j.transport_fee ? Math.round(j.total_amount).toLocaleString() : "—"} starting
                                        </span>
                                    </div>
                                    <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
                                        <span className="flex items-center gap-1"><MapPin size={10} strokeWidth={1.5} /> {j.city}</span>
                                        {j.eta_minutes && (
                                            <span className="flex items-center gap-1"><Clock size={10} strokeWidth={1.5} /> {j.eta_minutes}min ETA</span>
                                        )}
                                    </div>
                                    {myBid && (
                                        <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-amber-300">
                                            Your bid: ${Math.round(myBid.price).toLocaleString()} JMD · {myBid.status}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    size="sm"
                                    onClick={() => setBidModalJob({ ...j, myBid })}
                                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 px-4"
                                    data-testid={`bid-cta-${j.id}`}
                                >
                                    {myBid ? "Counter" : "Submit Bid"} <ArrowRight size={12} className="ml-1" strokeWidth={1.5} />
                                </Button>
                            </li>
                        );
                    })}
                </ul>
            </div>

            <BidModal
                job={bidModalJob}
                onClose={() => setBidModalJob(null)}
                onSaved={() => {
                    setBidModalJob(null);
                    refresh();
                }}
            />
        </div>
    );
}

function BidModal({ job, onClose, onSaved }) {
    const [price, setPrice] = useState("");
    const [hourly, setHourly] = useState("");
    const [eta, setEta] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Reset on open
    useEffect(() => {
        if (!job) return;
        setPrice(job.myBid?.price ? String(Math.round(job.myBid.price)) : String(Math.round(job.total_amount || 0)));
        setHourly(job.myBid?.hourly_rate ? String(job.myBid.hourly_rate) : "");
        setEta(job.myBid?.eta_minutes ? String(job.myBid.eta_minutes) : "");
        setMessage(job.myBid?.message || "");
    }, [job]);

    const submit = async (e) => {
        e.preventDefault();
        const p = parseFloat(price);
        if (!p || p <= 0) {
            toast.error("Enter a valid bid price");
            return;
        }
        setSubmitting(true);
        const payload = {
            price: p,
            hourly_rate: hourly ? parseFloat(hourly) : null,
            eta_minutes: eta ? parseInt(eta) : null,
            message: message.trim() || null,
        };
        try {
            if (job.myBid) {
                await api.post(`/bids/${job.myBid.id}/counter`, payload);
                toast.success("Counter-offer sent");
            } else {
                await api.post(`/bookings/${job.id}/bids`, payload);
                toast.success("Your Royal bid is live");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-card border-primary/30 sm:max-w-[460px]" data-testid="bid-modal">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                        {job?.myBid ? "Counter your bid" : "Submit your Royal bid"}
                    </DialogTitle>
                </DialogHeader>
                {job && (
                    <form onSubmit={submit} className="space-y-4">
                        <div className="bg-background/60 border border-white/10 rounded-sm p-3 text-xs">
                            <div className="text-muted-foreground uppercase tracking-[0.2em] mb-1">Job</div>
                            <div className="text-foreground">{job.service_name} · {job.city}</div>
                            <div className="text-muted-foreground mt-1">
                                Customer's starting price: <span className="gold-text">${Math.round(job.total_amount || 0).toLocaleString()} JMD</span>
                            </div>
                        </div>
                        <label className="block">
                            <div className="overline mb-1">Your total price (JMD) *</div>
                            <Input
                                type="number"
                                min="0"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                required
                                className="bg-input border-white/15 h-11 rounded-sm"
                                data-testid="bid-price-input"
                            />
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <div className="overline mb-1">Hourly rate (opt)</div>
                                <Input
                                    type="number"
                                    min="0"
                                    value={hourly}
                                    onChange={(e) => setHourly(e.target.value)}
                                    className="bg-input border-white/15 h-11 rounded-sm"
                                    data-testid="bid-hourly-input"
                                />
                            </label>
                            <label className="block">
                                <div className="overline mb-1">ETA (min)</div>
                                <Input
                                    type="number"
                                    min="0"
                                    max="1440"
                                    value={eta}
                                    onChange={(e) => setEta(e.target.value)}
                                    className="bg-input border-white/15 h-11 rounded-sm"
                                    data-testid="bid-eta-input"
                                />
                            </label>
                        </div>
                        <label className="block">
                            <div className="overline mb-1">Message (opt)</div>
                            <Textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                maxLength={600}
                                rows={3}
                                placeholder="What's included, your availability, any conditions"
                                className="bg-input border-white/15 rounded-sm"
                                data-testid="bid-message-input"
                            />
                        </label>
                        <DialogFooter className="gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-10 border-white/15"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-[10px] h-10"
                                data-testid="bid-submit-btn"
                            >
                                {submitting ? "Sending..." : job.myBid ? "Send Counter" : "Send Royal Bid"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
